// filepath: apps/web/src/features/lists/kanban/hooks/useKanbanMove.ts
//
// FR-UI-VIEW-1 AC-5 / AC-6 — useMutation hook for the Kanban
// drag-drop "move card to column" action.
//
// SHAPE
//   PUT /api/entity-records/{entityType}/{id} with body
//     { fields: { [groupByField]: toValue }, row_version: N }
//
// The server enforces INV-7 (row_version OCC). Three response paths:
//
//   1. 200 OK — record updated; server returns the new row_version +
//      the updated record (mirroring internal/entity/record_handlers.go
//      serveUpdate). The hook invalidates the Kanban cache so the next
//      re-render picks up the new column placement.
//
//   2. 409 version_conflict — server rejected because the local
//      row_version is stale. The 409 envelope carries `current_row_version`
//      so the SPA can wire up the FR-UI-CORE-6 conflict flow. The hook
//      throws an `HttpConflictError` and the parent (KanbanBoard)
//      opens `useConflictResolver.show()`. The row stays in the
//      pre-drag column until the conflict is resolved (no optimistic
//      state mutation on error — see AC-6).
//
//   3. 422 unprocessable_entity — server rejected because the column
//      value is not in the field's enum allowlist (FR-ENT-1 AC-2) or
//      the kanban group_by is misconfigured at definition time
//      (already-rejected at PutDefinition but a stale tenant could
//      surface here if the in-flight definition was rolled back). The
//      hook throws an `HttpValidationError` carrying a single-row
//      FieldErrorMap and the parent routes to the ValidationError
//      primitive.
//
// INVARIANTS
//   - INV-7: row_version is carried in the BODY (not If-Match) per
//     internal/entity/record_handlers.go UpdateRecordRequest.
//   - INV-2: the response body's record is server-stripped per
//     CanRead. We do NOT merge the local pending fields into the
//     returned record. The cache invalidation refetches the full
//     records set on the next render.
//   - Two other 4xx paths are mapped to typed errors so the caller
//     can decide whether to surface a fetch-failure, permission-
//     denied, or rate-limit surface.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import {
  ApiError,
  HttpConflictError,
  HttpValidationError,
  type FieldErrorMap,
} from "@/definitions/types";
import { apiFetch } from "@/api";
import { kanbanKeys } from "./useKanbanRecords";

/**
 * The mutation input — describes ONE drag-drop move. The caller
 * provides the original `fromValue` so we can roll back the local
 * optimistic state on a 4xx/5xx error (FR-UI-VIEW-1 AC-6 — no
 * silent column jumps).
 */
export interface KanbanMoveInput {
  /** UUID of the record being moved. */
  recordId: string;
  /** The enum field the Kanban groups by (FR-UI-VIEW-1 server contract). */
  groupByField: string;
  /** Target column value — must be one of the field's enum values. */
  toValue: string;
  /** Row version captured at drag-start (INV-7 OCC). */
  rowVersion: number;
}

export interface KanbanMoveError {
  /**
   * One of:
   *   - HttpConflictError → server returned 409
   *   - HttpValidationError → server returned 422
   *   - ApiError (any other non-2xx)
   *
   * The caller inspects the type via `instanceof` to route to the
   * right surface (conflict modal / ValidationError / generic
   * FetchError respectively).
   */
  error: ApiError;
  /**
   * The original mutation input. Surfaced so the parent can roll
   * back optimistic state without re-deriving the move.
   */
  input: KanbanMoveInput;
}

export interface UseKanbanMoveOptions {
  /** Entity type — used to build the PUT URL and the cache key. */
  entityType: string;
  /** Definition version driving the cache key. */
  viewConfigVersion: number;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
}

export type UseKanbanMoveResult = ReturnType<typeof useKanbanMove>;

/**
 * useKanbanMove — TanStack Query mutation for the drag-drop move.
 *
 * Usage (from KanbanColumn / KanbanBoard):
 *
 *   const move = useKanbanMove({ entityType, viewConfigVersion });
 *   move.mutate({ recordId, groupByField, toValue, rowVersion }, {
 *     onError: (err, input) => { rollBackLocalState(input); surfaceError(err); },
 *     onSuccess: () => { /* refetch already invalidated cache *\/ },
 *   });
 *
 * The hook DOES NOT call any surface-rendering code directly — the
 * parent owns the "route 409 → conflict modal; route 422 →
 * ValidationError; route other → FetchError" decision (per the
 * FR-UI-CORE-10 status envelope contract).
 */
export function useKanbanMove(opts: UseKanbanMoveOptions) {
  const { entityType, viewConfigVersion, apiBase = "/api" } = opts;
  const queryClient = useQueryClient();
  const { tenantSlug } = useTenant();

  const mutation = useMutation<
    void,
    ApiError,
    KanbanMoveInput,
    { previousKey: readonly unknown[] }
  >({
    // TanStack Query mutationKey (purely for devtools — invalidation
    // uses queryClient.invalidateQueries below).
    mutationFn: async (input: KanbanMoveInput) => {
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}/${encodeURIComponent(input.recordId)}`;

      const body = JSON.stringify({
        // INV-2 — we only send the group_by field. Other fields are
        // untouched; we use the server-stripped record as the source
        // of truth. A subsequent GET picks up the rest.
        fields: { [input.groupByField]: input.toValue },
        row_version: input.rowVersion,
      });

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // INV-7 row_version is forwarded via the body; the
        // optimistic-concurrency 409 + 422 paths are preserved
        // verbatim.
        response = await apiFetch(url, {
          method: "PUT",
          init: {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (response.ok) {
        return;
      }

      // All error paths share the same body parse.
      const errBody = await safeReadJson(response);
      const code =
        (errBody as { code?: string } | null)?.code ??
        `http_${response.status}`;
      const message =
        (errBody as { error?: string } | null)?.error ?? response.statusText;

      switch (response.status) {
        case 409: {
          // FR-ENT-8 / FR-UI-CORE-6 — the version-conflict envelope.
          // Mirrors internal/entity/record_handlers.go serveUpdate:
          //   { error, code: "version_conflict", current_row_version: N }
          // No `current_record` key — only the row_version is refreshed.
          const currentRowVersion = readCurrentRowVersion(errBody);
          throw new HttpConflictError(
            message,
            409,
            code,
            currentRowVersion,
            null,
          );
        }
        case 422: {
          // FR-UI-VIEW-1 AC-4 — the server returned a validation
          // envelope. When the wire is the per-field shape
          //   { errors: { field: { code, message } } }
          // the hook parses it into FieldErrorMap and wraps in an
          // HttpValidationError. Legacy shapes (a flat string) are
          // surfaced under the synthetic "__non_field_error__" key
          // so the ValidationError primitive renders them as one row.
          const errors = parseValidationErrors(errBody);
          throw new HttpValidationError(message, 422, code, errors);
        }
        case 403:
          throw new ApiError(message, 403, code);
        case 404:
          // Distinct from 403 for handlers that need to surface a
          // not-found modal (e.g. record deleted concurrently).
          throw new ApiError(message, 404, code);
        case 429: {
          const retryAfter = parseRetryAfter(
            response.headers.get("Retry-After"),
          );
          throw new ApiError(message, 429, code, retryAfter);
        }
        default:
          throw new ApiError(message, response.status, code);
      }
    },

    // AC-6: do NOT apply optimistic state — the renderer returns the
    // card to its original column on a 4xx/5xx (the parent owns the
    // rollback logic via onError). For now we invalidate the kanban
    // cache so any layout derived from `staleTime` flushes on the
    // next render; the manual update via PutMutation happens below.
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: kanbanKeys.window(tenantSlug, entityType, viewConfigVersion),
      });
    },
    onError: () => {
      // No cache invalidation on error — the roll-back is the parent's
      // responsibility (see KanbanDragDrop / KanbanBoard onError wiring).
    },
  });

  return mutation;
}

// ----------------- tiny helpers (local — not exported) ---------------

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  return null;
}

function readCurrentRowVersion(body: unknown): number | null {
  const raw = (body as { current_row_version?: unknown } | null)
    ?.current_row_version;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  return null;
}

/**
 * Parse a 422 wire envelope into a FieldErrorMap. The expected shape is
 *   { errors: { fieldId: { code: string, message: string | {locale: value} } } }
 *
 * The FR-UI-VIEW-1 AC-4 single-line server envelope ("invalid_kanban_group_by"
 * — single sentinel string, no per-field detail) is normalised to a one-row
 * FieldErrorMap under the synthetic "__non_field_error__" key so the
 * ValidationError primitive can render it as a single card.
 */
function parseValidationErrors(body: unknown): FieldErrorMap {
  const errorsBlock = (body as { errors?: unknown } | null)?.errors;
  if (errorsBlock && typeof errorsBlock === "object") {
    const out: FieldErrorMap = {};
    for (const [fieldId, entry] of Object.entries(
      errorsBlock as Record<string, unknown>,
    )) {
      if (entry && typeof entry === "object") {
        const e = entry as { code?: unknown; message?: unknown };
        if (typeof e.code === "string" && typeof e.message === "string") {
          out[fieldId] = { code: e.code, message: e.message };
          continue;
        }
      }
      // Defensive — malformed entry, surface as a plain text row.
      if (typeof entry === "string") {
        out[fieldId] = { code: "invalid", message: entry };
      }
    }
    if (Object.keys(out).length > 0) return out;
  }
  // Legacy flat-string or empty envelope — synthesise a non-field row.
  const fallbackMessage =
    (body as { error?: unknown } | null)?.error ??
    "Server returned 422 without a per-field envelope";
  if (typeof fallbackMessage === "string" && fallbackMessage !== "") {
    return {
      __non_field_error__: {
        code: "validation_failed",
        message: fallbackMessage,
      },
    };
  }
  return {};
}
